<main class="legal">

  <!-- Hero -->
  <section class="legal-hero">
    <div class="legal-hero-inner">
      <span class="legal-eyebrow">Privacy</span>
      <h1>Privacy Policy</h1>
      <p class="legal-meta">Last updated <time datetime="2024-07-14">July 14, 2024</time></p>
    </div>
  </section>

  <!-- Body: TOC sidebar + content -->
  <section class="legal-body">
    <div class="legal-body-inner">

      <!-- Table of Contents (sidebar on desktop, collapsible on mobile) -->
      <aside class="legal-toc" aria-label="Table of contents">
        <details class="legal-toc-collapse">
          <summary class="legal-toc-title">Contents</summary>
          <nav class="legal-toc-nav">
            <a href="privacy#information-we-collect">1. Information We Collect</a>
            <a href="privacy#how-we-use-your-information">2. How We Use Your Information</a>
            <a href="privacy#sharing-your-information">3. Sharing Your Information</a>
            <a href="privacy#user-generated-content">4. User-Generated Content</a>
            <a href="privacy#advertising">5. Advertising on GitCharger</a>
            <a href="privacy#your-choices">6. Your Choices</a>
            <a href="privacy#security">7. Security</a>
            <a href="privacy#childrens-privacy">8. Children's Privacy</a>
            <a href="privacy#changes">9. Changes to This Policy</a>
            <a href="privacy#contact-us">10. Contact Us</a>
          </nav>
        </details>
      </aside>

      <!-- Document content -->
      <article class="legal-content">

        <p class="legal-summary">
          GitCharger Inc. ("GitCharger," "we," "us," or "our") helps Electric Vehicle (EV)
          users locate chargers around them. Your privacy is important to us. This
          Privacy Policy explains how we collect, use, disclose, and protect your
          information when you use our app and website. By using our services, you
          agree to the terms of this Privacy Policy.
        </p>

        <h2 id="information-we-collect">1. Information We Collect</h2>
        <ul>
          <li>
            <b>Personal Information:</b> We may collect personal information such as
            a name, username, and email when you register for an account or contact
            us for support.
          </li>
          <li>
            <b>Location Data:</b> With your consent, we collect and process location
            data to provide map-based services, including trip planning and charging
            station locations.
          </li>
          <li>
            <b>Usage Data:</b> We collect information about you anonymously on how
            you use our app and website, including your interactions with the interface,
            the features you use, and the time spent on the app and website.
          </li>
          <li>
            <b>Device Information:</b> We may collect information about your device,
            including the type of device, operating system, and unique device
            identifiers.
          </li>
        </ul>

        <h2 id="how-we-use-your-information">2. How We Use Your Information</h2>
        <ul>
          <li>
            <b>To Provide Services:</b> We use your information to operate, maintain,
            and improve our app and website, including providing trip planning and
            charging station location services.
          </li>
          <li>
            <b>To Communicate:</b> We may use your contact information to send you
            updates, notifications, and support messages.
          </li>
          <li>
            <b>To Improve User Experience:</b> We use usage data to understand how
            our app and website are used and to enhance functionality.
          </li>
          <li>
            <b>For Security:</b> We use your information to detect and prevent fraud,
            abuse, and other harmful activities.
          </li>
        </ul>

        <h2 id="sharing-your-information">3. Sharing Your Information</h2>
        <ul>
          <li>
            <b>Legal Requirements:</b> We may disclose your information if required to
            do so by law or in response to valid requests by public authorities.
          </li>
          <li>
            <b>Business Transfers:</b> In the event of a merger, acquisition, or sale
            of assets, your information may be transferred to the new owner.
          </li>
        </ul>

        <!-- DRAFT - added 2026-05-13. See docs/privacy-terms-review.md. Counsel review required. -->
        <h2 id="user-generated-content">4. User-Generated Content</h2>
        <p>
          Parts of GitCharger let you contribute information that other users can see:
        </p>
        <ul>
          <li>
            <b>Community chat</b> at each charging station, where messages you post are
            visible to all drivers viewing that station.
          </li>
          <li>
            <b>Ratings and reviews</b> of stations.
          </li>
          <li>
            <b>Photos</b> you upload of stations.
          </li>
          <li>
            <b>Station contributions</b> include new stations you submit and edits to existing ones.
          </li>
        </ul>
        <p>
          When you post this content, you understand it is <b>public to the GitCharger
          community</b>. We may keep this content even after you delete your account, but
          we will dissociate it from your personal information (your name is replaced
          with a generic label such as "Former user").
        </p>
        <p>
          See our <a routerLink="/terms">Terms of Use</a> for the license you grant us when
          you post content, and the rules around what content is permitted.
        </p>

        <!-- DRAFT - added 2026-05-13. See docs/privacy-terms-review.md. Counsel review required. -->
        <h2 id="advertising">5. Advertising on GitCharger</h2>
        <p>
          GitCharger Inc. operates an advertising platform under the GitCharger Ads
          product, used by brands to reach EV drivers in the driver app and on the
          GitCharger map.
        </p>
        <p>
          When we serve ads, we may use <b>aggregated and de-identified driver data</b>
          (such as broad location areas, vehicle make, and charging-frequency segments)
          to help advertisers target relevant audiences. <b>We do not sell or share
          individual user profiles with advertisers.</b>
        </p>
        <p>
          For full details on how the GitCharger Ads platform handles data, targeting,
          and measurement, see the <a href="https://business.gitcharger.com/advertising-policy" target="_blank" rel="noopener">Advertising Policy</a>
          on the GitCharger Ads site.
        </p>

        <h2 id="your-choices">6. Your Choices</h2>
        <ul>
          <li>
            <b>Account Information:</b> You can update, correct, or delete your account
            information at any time by logging into your account.
          </li>
          <li>
            <b>Location Data:</b> You can enable or disable location services through
            your device settings.
          </li>
          <li>
            <b>Communication Preferences:</b> You can opt out of receiving promotional
            communications from us by following the unsubscribe instructions in those
            messages.
          </li>
        </ul>

        <h2 id="security">7. Security</h2>
        <p>
          We implement appropriate technical and organizational measures to protect
          your information from unauthorized access, use, or disclosure.
        </p>

        <h2 id="childrens-privacy">8. Children's Privacy</h2>
        <p>
          Our app and website are not intended for children under the age of 13. We do
          not knowingly collect personal information from children under 13. If we
          learn that we have collected such information, we will delete it.
        </p>

        <h2 id="changes">9. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of
          any changes by posting the new Privacy Policy on our app and website. Your
          continued use of our services after the changes take effect signifies your
          acceptance of the new policy.
        </p>

        <h2 id="contact-us">10. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, please reach us via
          our <a routerLink="/contact">contact page</a>.
        </p>

      </article>

    </div>
  </section>

</main>
